if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtons = [];
        this.submitButtonCandidates = [];
        this.submitButton = null;
        this.submitButtonText = null;
        this.mobileStickySubmitButtons = [];
        this.refreshSubmitButtons();
        if (!this.submitButton) return;
        this.forwardedSubmitButton = null;
        this.forwardedSubmitButtonResetTimer = null;
        this.mobileStickySubmitSyncFrame = 0;
        this.mobileStickySubmitButtons.forEach((button) =>
          button.addEventListener('click', this.onMobileStickySubmitClick.bind(this))
        );
        this.mobileStickySubmitObserver = null;
        this.syncMobileStickySubmitButtons();
        if (this.mobileStickySubmitButtons.length) {
          this.mobileStickySubmitObserver = new MutationObserver((records) => {
            // Proxy-only changes do not change the primary button state.
            const sourceChanged = records.some((record) => {
              const target = record.target.nodeType === Node.ELEMENT_NODE
                ? record.target
                : record.target.parentElement;
              return !target?.closest('.product-form__mobile-sticky');
            });
            if (sourceChanged) this.scheduleMobileStickySubmitButtonsSync();
          });
          this.mobileStickySubmitObserver.observe(this.form, {
            attributes: true,
            attributeFilter: ['aria-disabled', 'class', 'disabled', 'style', 'value'],
            childList: true,
            characterData: true,
            subtree: true,
          });
        }
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () =>
          this.scheduleMobileStickySubmitButtonsSync()
        );
        this.mobileStickyVisibilityObserver = null;
        if (this.mobileStickySubmitButtons.length) this.observeMobileStickyVisibility();
        this.observeInboxPurchaseClearance();

        if (document.querySelector('cart-drawer')) {
          this.submitButtons.forEach((button) => button.setAttribute('aria-haspopup', 'dialog'));
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      disconnectedCallback() {
        this.cleanupInboxPurchaseClearance?.();
        this.mobileStickySubmitObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.mobileStickyVisibilityObserver?.disconnect();
        if (this.mobileStickySubmitSyncFrame) {
          cancelAnimationFrame(this.mobileStickySubmitSyncFrame);
          this.mobileStickySubmitSyncFrame = 0;
        }
        clearTimeout(this.forwardedSubmitButtonResetTimer);
      }

      // Hide the mobile sticky bar while the in-page purchase buttons are on screen, so shoppers
      // never see two add-to-cart buttons at once. App-controlled clones live in the same container.
      observeMobileStickyVisibility() {
        const stickyBar = this.querySelector('.product-form__mobile-sticky');
        const purchaseButtons = this.querySelector('.product-form__buttons');
        if (!stickyBar || !purchaseButtons || !('IntersectionObserver' in window)) return;

        this.mobileStickyVisibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          stickyBar.classList.toggle('product-form__mobile-sticky--covered', entry.isIntersecting);
        });
        this.mobileStickyVisibilityObserver.observe(purchaseButtons);
      }

      // Keep the closed Inbox launcher clear of both purchase areas without narrowing them.
      // Translate the closed group without changing the app's layout-based bottom offset.
      // The exported dialog part counter-translates so the conversation stays in place.
      observeInboxPurchaseClearance() {
        if (!this.closest('.product__info-container') || !this.mobileStickySubmitButtons.length) return;
        const property = '--mori-inbox-purchase-shift';
        const marker = 'data-mori-inbox-clearance';
        const media = window.matchMedia('(max-width: 749px)');
        let frame = 0;
        let host = null;
        let original = null;
        let applied = null;
        let shift = 0;
        let stopped = false;
        let suspended = false;
        const observedRoots = new WeakSet();
        const restore = () => {
          if (host && original && applied !== null && host.style.getPropertyValue(property) === applied && !host.style.getPropertyPriority(property)) {
            if (original.value) host.style.setProperty(property, original.value, original.priority);
            else host.style.removeProperty(property);
            if (original.marker === null) host.removeAttribute(marker);
            else host.setAttribute(marker, original.marker);
          }
          applied = null;
          shift = 0;
        };
        const schedule = () => {
          if (!stopped && !frame) frame = requestAnimationFrame(update);
        };
        const shadowObserver = new MutationObserver(schedule);
        const observeRoot = (root) => {
          if (root && !observedRoots.has(root)) {
            observedRoots.add(root);
            shadowObserver.observe(root, {
              childList: true, subtree: true, attributes: true,
              attributeFilter: ['aria-expanded', 'open', 'class'],
            });
          }
        };
        const update = () => {
          frame = 0;
          if (!this.isConnected || !media.matches) { restore(); return; }
          const currentHost = document.querySelector('shopify-chat');
          if (currentHost !== host) {
            restore();
            host = currentHost;
            suspended = false;
            original = null;
          }
          if (!host || suspended) return;
          // Do not overwrite another editor/app's inline adjustment.
          if (applied !== null && (host.style.getPropertyValue(property) !== applied || host.style.getPropertyPriority(property))) {
            applied = null;
            shift = 0;
            suspended = true;
            return;
          }
          try {
            observeRoot(host.shadowRoot);
            const runtime = host.shadowRoot?.querySelector('shopify-agent-runtime');
            // The app must expose a dialog part that can cancel the group's translation.
            if (!runtime?.getAttribute('exportparts')?.split(',').some((part) => part.trim() === 'dialog')) {
              restore(); return;
            }
            observeRoot(runtime?.shadowRoot);
            const root = runtime?.shadowRoot || host.shadowRoot;
            const launcher = root?.querySelector('button[part~="activator"]');
            if (!launcher) { restore(); return; }
            // Do not translate the open conversation or its full-screen backdrop.
            if (host.hasAttribute('open') || runtime?.hasAttribute('open') ||
                launcher.getAttribute('aria-expanded') === 'true') {
              if (applied !== null) host.setAttribute(marker, 'open');
              return;
            }
            // Restore the closed group's previous placement before the app restores focus.
            if (applied !== null) host.setAttribute(marker, 'closed');
            if (root.activeElement) return;
            const rect = launcher.getBoundingClientRect();
            if (!rect.width || !rect.height) { restore(); return; }
            const base = { left: rect.left, right: rect.right, top: rect.top + shift, bottom: rect.bottom + shift };
            const viewport = window.visualViewport;
            const top = (viewport?.offsetTop || 0) + 12;
            const bottom = (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight);
            const obstacles = Array.from(this.querySelectorAll('.product-form__buttons, .product-form__mobile-sticky'))
              .map((element) => {
                const box = element.getBoundingClientRect();
                if (element.classList.contains('product-form__mobile-sticky')) {
                  // Reserve the final sticky footprint even while it is covered or sliding.
                  // Otherwise its 0.2s hide/show transition makes chat drop then rise again.
                  const height = element.offsetHeight;
                  return height > 0 ? {
                    left: box.left, right: box.right,
                    top: window.innerHeight - height, bottom: window.innerHeight,
                  } : null;
                }
                return this.isVisibleElement(element) ? box : null;
              })
              .filter((box) => box && box.bottom > top && box.top < bottom);
            const nextShift = this.inboxClearanceShift(base, obstacles, top);
            if (nextShift === null || nextShift === 0) { restore(); return; }
            const next = `${Math.ceil(nextShift)}px`;
            // A previous clearance session may have yielded to desktop or an idle app.
            // Snapshot the current unowned values at acquisition, not just host discovery.
            if (applied === null) original = {
              value: host.style.getPropertyValue(property),
              priority: host.style.getPropertyPriority(property),
              marker: host.getAttribute(marker),
            };
            host.setAttribute(marker, 'closed');
            if (next !== applied) host.style.setProperty(property, next);
            applied = next;
            shift = parseFloat(next);
          } catch (_) {
            // A future Inbox DOM change must not break purchasing or hide chat.
            restore();
          }
        };
        const formObserver = new MutationObserver(schedule);
        formObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        const bodyObserver = new MutationObserver(schedule);
        bodyObserver.observe(document.body, { childList: true });
        const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
        resizeObserver?.observe(this);
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        window.addEventListener('load', schedule);
        window.visualViewport?.addEventListener('resize', schedule);
        window.visualViewport?.addEventListener('scroll', schedule);
        document.addEventListener('focusout', schedule);
        media.addEventListener('change', schedule);
        this.cleanupInboxPurchaseClearance = () => {
          stopped = true;
          cancelAnimationFrame(frame);
          restore();
          shadowObserver.disconnect();
          formObserver.disconnect();
          bodyObserver.disconnect();
          resizeObserver?.disconnect();
          window.removeEventListener('scroll', schedule);
          window.removeEventListener('resize', schedule);
          window.removeEventListener('load', schedule);
          window.visualViewport?.removeEventListener('resize', schedule);
          window.visualViewport?.removeEventListener('scroll', schedule);
          document.removeEventListener('focusout', schedule);
          media.removeEventListener('change', schedule);
        };
        customElements.whenDefined('shopify-chat').then(schedule);
        schedule();
      }

      inboxClearanceShift(base, obstacles, minimumTop) {
        const gap = 12;
        let shift = 0;
        // Each upward move clears at least one obstacle; bounded to avoid scroll loops.
        for (let pass = 0; pass <= obstacles.length; pass++) {
          const collision = obstacles.find((box) =>
            base.left < box.right + gap && base.right > box.left - gap &&
            base.top - shift < box.bottom + gap && base.bottom - shift > box.top - gap
          );
          if (!collision) return shift;
          shift = base.bottom - collision.top + gap;
          if (base.top - shift < minimumTop) return null;
        }
        return null;
      }


      onMobileStickySubmitClick(evt) {
        evt.preventDefault();
        const stickyButton = evt.currentTarget;
        const submitButton = this.getSubmitButtonForMobileStickyClick({ refresh: true });
        if (stickyButton.disabled || stickyButton.getAttribute('aria-disabled') === 'true') return;
        if (!submitButton || submitButton === stickyButton) return;
        if (submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

        this.forwardedSubmitButton = stickyButton;
        clearTimeout(this.forwardedSubmitButtonResetTimer);
        submitButton.click();
        this.forwardedSubmitButtonResetTimer = setTimeout(() => {
          if (this.forwardedSubmitButton === stickyButton) this.forwardedSubmitButton = null;
        }, 8000);
      }

      getSubmitButtonForMobileStickyClick({ refresh = false } = {}) {
        if (refresh) this.refreshSubmitButtons();
        const submitCandidates = this.submitButtonCandidates.length
          ? this.submitButtonCandidates
          : this.getSubmitButtonCandidates();
        const appControlledButton = submitCandidates.find(
          (button) => button !== this.submitButton && this.isVisibleElement(button)
        );

        return appControlledButton || this.submitButton;
      }

      getSubmitButtonCandidates() {
        if (!this.form) return [];

        return Array.from(
          this.form.querySelectorAll('button[type="submit" i], input[type="submit" i], [name="add"]')
        ).filter((button) => !button.classList.contains('product-form__mobile-sticky-submit'));
      }

      isVisibleElement(element) {
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }

      refreshSubmitButtons() {
        this.dedupeSubmitButtonIds();
        this.submitButtons = Array.from(
          this.querySelectorAll('.product-form__submit, .product-form__mobile-sticky-submit, .quick-add__submit')
        );
        this.submitButtonCandidates = this.getSubmitButtonCandidates();
        this.mobileStickySubmitButtons = this.submitButtons.filter((button) =>
          button.classList.contains('product-form__mobile-sticky-submit')
        );

        const primarySubmitButtons = this.submitButtons.filter(
          (button) => !button.classList.contains('product-form__mobile-sticky-submit')
        );
        // With one candidate, both the visible and fallback paths select the same button.
        this.submitButton = primarySubmitButtons.length === 1
          ? primarySubmitButtons[0]
          : primarySubmitButtons.find((button) => this.isVisibleElement(button)) || primarySubmitButtons[0];
        this.submitButtonText = this.submitButton?.querySelector('span') || null;
      }

      dedupeSubmitButtonIds() {
        const controlsById = new Map();
        const controls = Array.from(
          this.querySelectorAll('.product-form__submit, .quick-add__submit, button[name="add"], input[name="add"]')
        ).filter((button) => !button.classList.contains('product-form__mobile-sticky-submit'));

        controls.forEach((button) => {
          if (!button.id) return;
          if (!controlsById.has(button.id)) controlsById.set(button.id, []);
          controlsById.get(button.id).push(button);
        });

        controlsById.forEach((buttons) => {
          if (buttons.length < 2) return;
          const preferredButton = buttons.find((button) => this.isVisibleElement(button)) || buttons[0];
          buttons.forEach((button) => {
            if (button !== preferredButton) button.removeAttribute('id');
          });
        });
      }

      scheduleMobileStickySubmitButtonsSync() {
        if (this.mobileStickySubmitSyncFrame) return;
        this.mobileStickySubmitSyncFrame = requestAnimationFrame(() => {
          this.mobileStickySubmitSyncFrame = 0;
          this.refreshSubmitButtons();
          this.syncMobileStickySubmitButtons();
        });
      }

      // Syncs the backorder color on the theme submit button, then mirrors state onto the optional sticky proxy.
      syncMobileStickySubmitButtons() {
        if (!this.submitButton) return;

        const sourceButton = this.getSubmitButtonForMobileStickyClick();
        if (!sourceButton) return;
        const sourceText = sourceButton.querySelector('span');
        const sourceAriaDisabled = sourceButton.getAttribute('aria-disabled');
        const sourceDisabled = sourceButton.hasAttribute('disabled');
        const isBackorder = !sourceDisabled && this.isSelectedVariantBackorder();
        // App clones do not always render, so the theme's own button carries the backorder color too.
        this.submitButtons.forEach((button) => {
          if (button.classList.contains('product-form__submit')) {
            button.classList.toggle('product-form__submit--backorder', isBackorder);
          }
        });
        if (!this.mobileStickySubmitButtons.length) return;

        this.mobileStickySubmitButtons.forEach((button) => {
          if (button === sourceButton) return;

          button.classList.toggle('product-form__mobile-sticky-submit--backorder', isBackorder);

          const buttonText = button.querySelector('span');
          if (sourceText && buttonText && buttonText.innerHTML !== sourceText.innerHTML) {
            buttonText.innerHTML = sourceText.innerHTML;
          }

          if (button.hasAttribute('disabled') !== sourceDisabled) {
            button.toggleAttribute('disabled', sourceDisabled);
          }
          if (sourceAriaDisabled === null) {
            if (button.hasAttribute('aria-disabled')) button.removeAttribute('aria-disabled');
          } else if (button.getAttribute('aria-disabled') !== sourceAriaDisabled) {
            button.setAttribute('aria-disabled', sourceAriaDisabled);
          }
        });
      }

      // Every picker type (button, swatch, dropdown) puts data-option-state on the input or option itself;
      // the checked value reflects the selected variant. Without a picker the variant cannot change in place,
      // so the server-rendered flag on this form stays accurate.
      isSelectedVariantBackorder() {
        const scope = this.closest('product-info') || document;
        const picker = scope.querySelector('variant-selects');
        if (!picker) return this.dataset.selectedVariantBackorder === 'true';
        return Boolean(
          picker.querySelector(
            'input:checked[data-option-state="backorder"], option:checked[data-option-state="backorder"]'
          )
        );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.refreshSubmitButtons();
        const forwardedSubmitButton = this.forwardedSubmitButton;
        this.forwardedSubmitButton = null;
        clearTimeout(this.forwardedSubmitButtonResetTimer);
        const activeSubmitButton = this.submitButtons.includes(forwardedSubmitButton)
          ? forwardedSubmitButton
          : this.submitButtons.includes(evt.submitter)
            ? evt.submitter
            : this.submitButtonCandidates.includes(evt.submitter)
              ? evt.submitter
              : this.submitButton;
        if (!activeSubmitButton || activeSubmitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButtons.forEach((button) => button.setAttribute('aria-disabled', true));
        activeSubmitButton.setAttribute('aria-disabled', true);
        activeSubmitButton.classList.add('loading');
        activeSubmitButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => this.parseCartAddResponse(response))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            const message = e.message || window.cartStrings?.error || 'Unable to add item to cart. Please try again.';
            this.handleErrorMessage(message);
            this.error = false;
            this.dispatchCartErrorEvent(message, e.code || 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            activeSubmitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButtons.forEach((button) => button.removeAttribute('aria-disabled'));
              activeSubmitButton.removeAttribute('aria-disabled');
            }
            activeSubmitButton.querySelector('.loading__spinner')?.classList.add('hidden');
            this.syncMobileStickySubmitButtons();

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButtons.forEach((button) => {
          const buttonText = button.querySelector('span');
          if (disable) {
            if (!button.hasAttribute('disabled')) button.setAttribute('disabled', 'disabled');
            if (text && buttonText) buttonText.textContent = text;
          } else {
            if (button.hasAttribute('disabled')) button.removeAttribute('disabled');
            if (buttonText) buttonText.textContent = window.variantStrings.addToCart;
          }
        });
        this.syncMobileStickySubmitButtons();
      }

      async parseCartAddResponse(response) {
        const contentType = response.headers?.get?.('content-type') || '';
        if (contentType.includes('application/json')) {
          try {
            const payload = await response.json();
            if (!response.ok && !payload?.status) {
              throw new Error(this.cartAddErrorMessage(response));
            }
            return payload;
          } catch (error) {
            throw Object.assign(new Error(this.cartAddErrorMessage(response)), {
              code: response.ok ? 'INVALID' : 'SERVICE_UNAVAILABLE',
              cause: error,
            });
          }
        }

        const text = await response.text().catch(() => '');
        if (text) {
          try {
            const payload = JSON.parse(text);
            if (!response.ok && !payload?.status) {
              throw new Error(this.cartAddErrorMessage(response));
            }
            return payload;
          } catch (error) {
            throw Object.assign(new Error(this.cartAddErrorMessage(response)), {
              code: response.ok ? 'INVALID' : 'SERVICE_UNAVAILABLE',
              cause: error,
            });
          }
        }

        throw Object.assign(new Error(this.cartAddErrorMessage(response)), {
          code: response.ok ? 'INVALID' : 'SERVICE_UNAVAILABLE',
        });
      }

      cartAddErrorMessage(response) {
        const fallback = window.cartStrings?.error || 'Unable to add item to cart. Please try again.';
        if (response?.status === 429) return fallback;
        if (response?.status >= 500) return fallback;
        return fallback;
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.js`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
